import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import { CaretDown, ArrowRight } from "@phosphor-icons/react";

const FAQS = [
  {
    q: "What is Vintage Vantage?",
    a: (
      <p>
        Vintage Vantage is an AI-powered inventory and cross-listing platform built specifically for vintage
        furniture, art, antiques, and one-of-a-kind pieces. It helps you build your inventory once, then
        reuse it everywhere.
      </p>
    ),
  },
  {
    q: "Who is Vintage Vantage for?",
    a: (
      <>
        <p>Vintage Vantage is designed for:</p>
        <ul>
          <li>New vintage sellers looking for guidance and confidence.</li>
          <li>Experienced sellers managing growing inventories.</li>
          <li>Professional dealers who want to save time, improve consistency, and expand their reach.</li>
        </ul>
      </>
    ),
  },
  {
    q: "How is Vintage Vantage different from other listing tools?",
    a: (
      <>
        <p>Most listing tools simply copy information from one marketplace to another. Vintage Vantage goes much further by helping you:</p>
        <ul>
          <li>Build a seller-owned inventory.</li>
          <li>Recover and organize your existing listing information.</li>
          <li>Create AI-assisted drafts for new finds.</li>
          <li>Cross-list across multiple marketplaces.</li>
          <li>Continue improving your listings over time.</li>
        </ul>
        <p>The goal isn't just faster listings. <strong>It's building an inventory that becomes more valuable every time you use it.</strong></p>
      </>
    ),
  },
  {
    q: "Do I own my inventory?",
    a: (
      <p>
        <strong>Yes. Your inventory belongs to you.</strong> Vintage Vantage helps organize, enhance, and
        manage your listing information so your work isn't trapped on a single marketplace.
      </p>
    ),
  },
  {
    q: "Which marketplaces are supported?",
    a: (
      <p>
        Vintage Vantage is designed to work with leading vintage marketplaces, with support expanding over
        time. Current integrations include platforms such as <strong>Chairish, 1stDibs, AptDeco, and Pamono</strong>,
        with additional marketplaces planned. Because your records live with you — not inside any one
        platform — adding a new marketplace later never means re-typing your catalog.
      </p>
    ),
  },
  {
    q: "How does AI help me?",
    a: (
      <>
        <p>AI helps reduce repetitive work by assisting with:</p>
        <ul>
          <li>Draft creation</li>
          <li>Item identification</li>
          <li>Listing organization</li>
          <li>Description suggestions</li>
          <li>Inventory management</li>
          <li>Cross-list preparation</li>
        </ul>
        <p>Your expertise still matters. <strong>AI assists your workflow — it doesn't replace your judgment.</strong> And its suggestions draw on the inventory you've already built, so your past work keeps paying forward.</p>
      </>
    ),
  },
  {
    q: "Can AI identify vintage furniture?",
    a: (
      <p>
        Yes. Using image recognition, Vintage Vantage can help identify many vintage pieces and generate a
        draft listing to save you significant research and typing time. You always review and approve the
        information before publishing.
      </p>
    ),
  },
  {
    q: "Can I edit everything?",
    a: (
      <p>
        Absolutely. Every AI-generated suggestion can be edited before you publish.{" "}
        <strong>You remain in complete control of your listings.</strong> And every refinement is saved to
        your own record — improve a description once, and every future export carries it.
      </p>
    ),
  },
  {
    q: "Why isn't everything fully automated?",
    a: (
      <p>
        Because vintage selling isn't standardized. Condition, provenance, restoration, rarity, and market
        demand all require human expertise. Vintage Vantage is designed to{" "}
        <strong>amplify your experience — not replace it.</strong>
      </p>
    ),
  },
  {
    q: "What happens after I import my inventory?",
    a: (
      <>
        <p>Once your inventory is imported, Vintage Vantage helps organize it into your own inventory management system, where you can:</p>
        <ul>
          <li>Update records</li>
          <li>Improve descriptions</li>
          <li>Prepare listings for multiple marketplaces</li>
          <li>Track your inventory more efficiently</li>
        </ul>
        <p>Nothing is lost in the move — the descriptions, photos, and details you already published are recovered into records you keep.</p>
      </>
    ),
  },
  {
    q: "Does Vintage Vantage replace marketplaces?",
    a: (
      <p>
        No. Vintage Vantage works alongside your favorite marketplaces. It helps you prepare, organize, and
        manage your listings while giving you greater control over your own inventory.
      </p>
    ),
  },
  {
    q: "Why was Vintage Vantage created?",
    a: (
      <>
        <p>
          Vintage selling is incredibly rewarding — but listing one-of-a-kind pieces can take hours. Vintage
          Vantage was built to eliminate unnecessary repetition while preserving the knowledge and expertise
          that make great vintage sellers successful.
        </p>
        <p>Our philosophy is simple: <strong>Technology should amplify human expertise — not replace it.</strong></p>
      </>
    ),
  },
  {
    q: "Is Vintage Vantage still improving?",
    a: (
      <p>
        Yes. Vintage Vantage is actively evolving based on real feedback from vintage sellers. Every update
        is focused on helping sellers spend less time on repetitive work and more time sourcing, selling,
        and growing their business. And improvements never cost you your history — your records carry
        forward through every update.
      </p>
    ),
  },
];

function FaqItem({ item, index, open, onToggle }) {
  return (
    <div className="border border-[#0D1B2A]/20 bg-white" data-testid={`faq-item-${index}`}>
      <button
        onClick={onToggle}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 hover:bg-[#F5F3ED] transition-colors"
      >
        <span className="font-serif-display text-xl sm:text-2xl font-bold tracking-tight text-[#0D1B2A]">{item.q}</span>
        <CaretDown size={20} weight="bold" className={`shrink-0 text-[#B47A3C] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-6 text-[15px] sm:text-base text-[#3A3730] leading-relaxed space-y-3 [&_ul]:list-none [&_ul]:space-y-1.5 [&_li]:pl-5 [&_li]:relative [&_li]:before:content-[''] [&_li]:before:absolute [&_li]:before:left-0 [&_li]:before:top-[9px] [&_li]:before:w-2 [&_li]:before:h-2 [&_li]:before:rotate-45 [&_li]:before:bg-[#B47A3C]" data-testid={`faq-answer-${index}`}>
          {item.a}
        </div>
      )}
    </div>
  );
}

export default function FaqPage() {
  const navigate = useNavigate();
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="faq-page">
      <PublicHeader />
      <main className="max-w-3xl mx-auto px-6 py-14">
        <p className="label-caps text-[#B47A3C] mb-2">Frequently asked questions</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-[1.05] font-medium mb-4">
          Everything you need to know — <span className="italic text-[#B47A3C]">at a glance.</span>
        </h1>
        <p className="text-lg text-[#525252] leading-relaxed mb-10">
          Straight answers, no legalese. Something we didn't cover?{" "}
          <a href="mailto:vintagevantageapp@gmail.com?subject=Vintage%20Vantage%20%E2%80%94%20question" data-testid="faq-contact-link" className="text-[#B47A3C] underline underline-offset-4 hover:text-[#0D1B2A]">Just ask.</a>
        </p>
        <div className="space-y-3" data-testid="faq-list">
          {FAQS.map((item, i) => (
            <FaqItem key={item.q} item={item} index={i} open={openIdx === i} onToggle={() => setOpenIdx(openIdx === i ? -1 : i)} />
          ))}
        </div>
        <div className="mt-12 border-2 border-[#0D1B2A] bg-[#0D1B2A] text-[#FDFBF7] p-8 text-center">
          <p className="font-serif-display text-2xl sm:text-3xl font-bold mb-2">Convinced? Your inventory is waiting.</p>
          <p className="text-[15px] text-[#D8D3C8] mb-5">Free to start — upload a marketplace export and watch it come back to life.</p>
          <button onClick={() => navigate("/register")} data-testid="faq-cta" className="h-13 px-8 py-3.5 bg-[#FDFBF7] text-[#0D1B2A] rounded-none font-medium text-[16px] inline-flex items-center gap-2 hover:bg-white transition-transform active:scale-95">
            Get started free <ArrowRight size={17} weight="bold" />
          </button>
        </div>
      </main>
    </div>
  );
}
